/**
 * winess-request-quote.js
 * Web Component for the Request a Quote modal form.
 */
class WinessRequestQuote extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('.winess-quote__trigger');
    this.template = this.querySelector('template');
  }

  connectedCallback() {
    this.trigger?.addEventListener('click', this.open.bind(this));
    this.maybeRestoreSuccess();
  }

  maybeRestoreSuccess() {
    // Shopify redirects to ?customer_posted=true after a successful customer
    // (newsletter) form post — same query param Shopify uses for the contact
    // form is `?contact_posted=true`. We accept either so this snippet keeps
    // working if we ever switch back. Show a transient toast confirming the
    // submission. The modal is not re-opened.
    try {
      const url = new URL(window.location.href);
      const isPosted = url.searchParams.get('customer_posted') === 'true'
        || url.searchParams.get('contact_posted') === 'true';
      if (!isPosted) return;
      const flag = sessionStorage.getItem('winessQuoteSuccess');
      if (!flag) return;
      sessionStorage.removeItem('winessQuoteSuccess');
      // Hand off to winess-custom.js conversion tracking BEFORE stripping the
      // param — this script runs first (document order), so without this flag
      // the Google Ads quote_request conversion would never see the success.
      try { sessionStorage.setItem('winess_contact_posted', window.location.pathname); } catch (e) { /* fail-soft */ }
      url.searchParams.delete('customer_posted');
      url.searchParams.delete('contact_posted');
      url.searchParams.delete('form_type');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      WinessRequestQuote.showToast('Thank you for your enquiry — we’ll be in touch shortly.');
    } catch (e) {
      /* fail-soft */
    }
  }

  static showToast(message, durationMs = 5000) {
    // Reuse the same toast container if multiple submissions happen on one page.
    let toast = document.querySelector('.winess-toast');
    if (toast) toast.remove();
    toast = document.createElement('div');
    toast.className = 'winess-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = message;
    document.body.appendChild(toast);
    // Force a layout flush so the entrance transition runs.
    requestAnimationFrame(() => toast.classList.add('winess-toast--visible'));
    setTimeout(() => {
      toast.classList.remove('winess-toast--visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      // Hard-remove after a safety timeout in case transitionend doesn't fire.
      setTimeout(() => toast.remove(), 1000);
    }, durationMs);
  }

  open() {
    if (this.modal) return;

    const fragment = this.template.content.cloneNode(true);
    this.appendChild(fragment);

    this.modal = this.querySelector('.winess-quote__modal');
    this.overlay = this.querySelector('.winess-quote__overlay');
    this.form = this.modal.querySelector('form');
    this.closeBtn = this.modal.querySelector('.winess-quote__close');
    this.successEl = this.modal.querySelector('.winess-quote__success');
    this.errorEl = this.modal.querySelector('.winess-quote__error');

    this.closeBtn.addEventListener('click', (e) => { e.stopPropagation(); this.close(); });
    this.overlay.addEventListener('click', (e) => { e.stopPropagation(); this.close(); });
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.handleKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', this.handleKeydown);

    // Auto-populate message with product name and selected variants
    const textarea = this.modal.querySelector('textarea[name="contact[body]"]');
    if (textarea) {
      const productTitle = this.dataset.productTitle || '';
      const variantInfo = this.getSelectedVariants();
      let msg = `Hi, I am interested to know more about the ${productTitle}`;
      if (variantInfo) msg += ` (${variantInfo})`;
      msg += '.';
      textarea.value = msg;
    }

    requestAnimationFrame(() => {
      this.modal.classList.add('winess-quote__modal--open');
      this.overlay.classList.add('winess-quote__overlay--open');
      document.body.style.overflow = '';
      this.modal.querySelector('input:not([type="hidden"])').focus();
    });
  }

  close() {
    if (!this.modal) return;

    this.modal.classList.remove('winess-quote__modal--open');
    this.overlay.classList.remove('winess-quote__overlay--open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handleKeydown);

    this.modal.addEventListener('transitionend', () => {
      this.modal?.remove();
      this.overlay?.remove();
      this.modal = null;
      this.overlay = null;
      this.trigger.focus();
    }, { once: true });
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  handleSubmit(event) {
    // Native form POST to /contact (Shopify contact form). Sends an email to
    // the store contact address with the structured quote body composed
    // below. Admin record creation is handled out-of-band via Shopify Flow.
    //
    // We submit natively (not fetch) because Shopify's bot protection
    // intercepts AJAX POSTs to /contact. A real browser submit lets the
    // user interactively pass any challenge if shown.

    const honeypot = this.form.querySelector('[name="winess_hp"]');
    if (honeypot && honeypot.value) {
      event.preventDefault();
      return;
    }

    // Prepend the product context to the message body. Everything else
    // (name/email/phone/postcode) is already its own contact[] field, which
    // Shopify lists in the notification email — composing them into the body
    // too caused duplicate lines in the merchant's inbox.
    try {
      const formData = new FormData(this.form);
      const bodyInput = this.form.querySelector('[name="contact[body]"]');
      if (bodyInput) bodyInput.value = this.buildEmailBody(formData);
    } catch (err) {
      console.warn('[winess-request-quote] failed to compose body', err);
    }

    const submitBtn = this.form.querySelector('.winess-quote__submit');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('winess-quote__submit--loading');
    }

    try {
      sessionStorage.setItem('winessQuoteSuccess', this.dataset.productTitle || '1');
    } catch (e) { /* private browsing / storage disabled */ }

    // Don't preventDefault — let the browser submit natively.
  }

  getSelectedVariants() {
    const parts = [];
    // Craft theme uses fieldsets with legend for each option (Size, Configuration, etc.)
    document.querySelectorAll('.product-form__input').forEach((fieldset) => {
      const legend = fieldset.querySelector('.form__label, legend');
      const selected = fieldset.querySelector('input:checked');
      if (legend && selected) {
        const label = legend.textContent.trim().replace(/:$/, '');
        const value = selected.value;
        if (value && value !== 'Default Title') parts.push(`${label}: ${value}`);
      }
    });
    // Fallback: check select dropdowns
    if (parts.length === 0) {
      document.querySelectorAll('.product-form__input select').forEach((sel) => {
        if (sel.value && sel.value !== 'Default Title') parts.push(sel.value);
      });
    }
    return parts.join(', ');
  }

  buildEmailBody(formData) {
    // Product context only — customer fields are listed by Shopify's email
    // template from their own contact[] inputs, so they don't belong here.
    const productTitle = this.form.querySelector('[data-winess-product-title]')?.value || this.dataset.productTitle || '';
    const productUrl = this.form.querySelector('[data-winess-product-url]')?.value || '';
    const variant = this.form.querySelector('[data-winess-variant]')?.value || '';
    const message = formData.get('contact[body]') || '';

    let body = `Quote Request — ${productTitle}`;
    if (productUrl) body += `\nURL: ${productUrl}`;
    if (variant && variant !== 'Default Title') body += `\nVariant: ${variant}`;
    if (message) body += `\n\n${message}`;
    return body;
  }
}

customElements.define('winess-request-quote', WinessRequestQuote);
